import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { organizationsApi, type Organization, type RequestDirection } from '@/api/organizations'
import { useAuthStore, useCan } from '@/store/auth'

export function useOrganizations(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['organizations'],
    queryFn: organizationsApi.list,
    enabled: opts?.enabled ?? true,
  })
}

export function useOrganization(orgId: number | undefined) {
  return useQuery({
    queryKey: ['organization', orgId],
    queryFn: () => organizationsApi.get(orgId!),
    enabled: !!orgId,
  })
}

export function useJiraOrgConfig(orgId: number | undefined) {
  return useQuery({
    queryKey: ['jira-org-config', orgId],
    queryFn: () => organizationsApi.getJiraConfig(orgId!),
    enabled: !!orgId,
    retry: false,
  })
}

export function useCreateOrganization() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: organizationsApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['organizations'] }),
  })
}

export function useGestores() {
  return useQuery({
    queryKey: ['gestores'],
    queryFn: organizationsApi.listGestores,
  })
}

export function useUpdateOrganization() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: number; data: { name?: string; description?: string; responsible_user_id?: number; requires_change_control?: boolean; ticket_code?: string; change_control_environments?: string[] } }) =>
      organizationsApi.update(orgId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['organizations'] }),
  })
}

export function useDeleteOrganization() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (orgId: number) => organizationsApi.delete(orgId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['organizations'] }),
  })
}

/**
 * Organizações que o usuário logado GERE (role='gestor' em organization_users
 * OU responsável da org — organizations.responsible_user_id).
 *
 * Espelha a parte não-admin do gate `_can_manage_org` do backend
 * (organization_service.py). Fonte de dados:
 * - `GET /v1/organizations` — o backend já escopa às orgs VISÍVEIS ao usuário
 *   (organization_ids_visible_to_user); para não-admin isso inclui orgs onde é
 *   apenas member, então filtramos client-side às que ele de fato gere.
 * - `GET /v1/organizations/{id}/users` — para detectar role='gestor' do
 *   próprio usuário (o /auth/me não expõe o papel por org).
 *
 * Isto é conveniência de UI (menu/tela) — as AÇÕES continuam protegidas pelo
 * backend (_can_manage_org + guards member-only da spec 2026-07-24 §3.2).
 * Para admin a query nem roda (enabled=false): admin vê tudo pelo fluxo atual.
 */
export function useManagedOrganizations() {
  const user = useAuthStore((s) => s.user)
  const can = useCan()
  const isAdmin = can('admin', 'platform')
  return useQuery({
    queryKey: ['managed-organizations', user?.id],
    enabled: !!user && !isAdmin,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Organization[]> => {
      const orgs = await organizationsApi.list()
      const checks = await Promise.all(
        orgs.map(async (org) => {
          if (org.responsible_user_id != null && org.responsible_user_id === user!.id) {
            return org
          }
          try {
            const orgUsers = await organizationsApi.listOrgUsers(org.id)
            const me = orgUsers.find((u) => u.user_id === user!.id)
            return me?.role === 'gestor' ? org : null
          } catch {
            return null
          }
        }),
      )
      return checks.filter((o): o is Organization => o != null)
    },
  })
}

// ── Solicitações de vínculo (Fase 2 — spec 2026-07-24 §3.1) ────────────────

/** Fila de solicitações de uma org (incoming/outgoing). */
export function useMembershipRequests(
  orgId: number | undefined,
  direction: RequestDirection = 'incoming',
  status: string = 'pending',
  enabled = true,
) {
  return useQuery({
    queryKey: ['membership-requests', orgId, direction, status],
    queryFn: () => organizationsApi.listMembershipRequests(orgId!, direction, status),
    enabled: !!orgId && enabled,
  })
}

/** Status de organização do usuário logado (tela sem-org). */
export function useMyOrganizationStatus(enabled = true) {
  return useQuery({
    queryKey: ['my-organization-status'],
    queryFn: organizationsApi.myOrganizationStatus,
    enabled,
    staleTime: 60_000,
  })
}

export function useLinkJira() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ orgId, jiraUrl }: { orgId: number; jiraUrl: string }) =>
      organizationsApi.linkJira(orgId, jiraUrl),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['jira-org-config', vars.orgId] })
    },
  })
}
